import React, {useEffect, useState} from "react";

import {ButtonContainer, LoaderContainer, override} from "@pages/reusable-components/styled-components";
import {Card, CardBody, CardHeader, Col, FormGroup, Input, Row} from "reactstrap";
import {InputKeySelect} from "@pages/components/InputSelect";
import InputCustom from "@pages/components/InputCustom";
import {validateForm} from "@constants/index";
import {ClipLoader} from "react-spinners";

const allowedFields = [
    'title',
    'first_name',
    'last_name',
    'dob',
    'attention_to',
    'email',
    'mobile_number',
    'bank_account_number',
    'full_name',
    'national_insurance_number',
    'driver_license_number',
    'country_of_issue',
    'licence_expiry_date',
    'licence_issue_date'
];
export default function Personal({ initialCustomer,
                                     titles,
                                     errors,
                                     onSave,
                                     goList,
                                     sendPasswordLink,}) {
    const [customer, setCustomer] = useState({});
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (initialCustomer) {
            const filtered = Object.fromEntries(
                allowedFields.map((key) => [key, initialCustomer[key] ?? ''])
            );
            setCustomer(filtered);
            setLoading(false)
        } else {
            const empty = Object.fromEntries(allowedFields.map((key) => [key, '']));
            setCustomer(empty);
        }
    }, [initialCustomer]);
    const update = (updates) => {
        setCustomer((prev) => ({
            ...prev,
            ...updates,
        }));
    };
    const handleSave = () => {
       onSave(customer);
    };

    const getValue = (key) => customer?.[key] || '';

    return (
        loading ? <LoaderContainer>
                <ClipLoader
                    css={override}
                    size={40}
                    color={`#7B61E4`}
                    loading={loading}
                />
            </LoaderContainer>
            : <Card>
            <CardHeader>
                <Row className="align-items-center justify-content-between">
                    <Col>
                        <h1 className="mb-0">Customer management</h1>
                    </Col>
                    <Col>
                        <Row className="align-items-center justify-content-end">
                            <label className="form-control-label mr-5">Send password link</label>
                            <ButtonContainer reverse onClick={sendPasswordLink}>Send</ButtonContainer>
                        </Row>
                    </Col>
                </Row>
            </CardHeader>

            <CardBody>
                <div className="mb-4" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <h2>Personal information</h2>
                </div>

                <Row className="phone-container">
                    <Col md={4}>
                        <InputKeySelect
                            newLabel="Title"
                            options={titles}
                            value={getValue('title')}
                            onChange={(e) => {
                                const title = e.target.value;
                                update({
                                    title,
                                    attention_to: `${title ? titles[title] + ' ' : ''}${getValue('first_name')} ${getValue('last_name')}`
                                });
                            }}
                            defaultOption
                            invalid={!!errors?.title}
                            errorMessage={errors?.title}
                        />
                        <InputCustom
                            newLabel="First Name"
                            placeholder="First Name"
                            value={getValue('first_name')}
                            onChange={(e) => {
                                const value = e.target.value;
                                update({
                                    first_name: value,
                                    full_name: `${value} ${getValue('last_name')}`,
                                    attention_to: `${getValue('title') ? titles[getValue('title')] + ' ' : ''}${value} ${getValue('last_name')}`
                                });
                            }}
                        />
                        <InputCustom
                            newLabel="Last Name"
                            placeholder="Last Name"
                            value={getValue('last_name')}
                            onChange={(e) => {
                                const value = e.target.value;
                                update({
                                    last_name: value,
                                    full_name: `${getValue('first_name')} ${value}`,
                                    attention_to: `${getValue('title') ? titles[getValue('title')] + ' ' : ''}${getValue('first_name')} ${value}`
                                });
                            }}
                        />
                        <InputCustom
                            type="date"
                            newLabel="Date of Birth"
                            value={getValue('dob')}
                            onChange={(e) => update({
                                'dob': e.target.value
                            })}
                            invalid={!!errors?.dob}
                            errorMessage={errors?.dob}
                        />
                    </Col>

                    <Col md={4}>
                        <InputCustom
                            newLabel="Attention To"
                            placeholder="Attention To"
                            value={getValue('attention_to')}
                            onChange={(e) => update({
                                'attention_to': e.target.value
                            })}
                        />
                        <InputCustom
                            newLabel="Email Address"
                            placeholder="Email Address"
                            checkEmail
                            checkValid={false}
                            required
                            value={getValue('email')}
                            onChange={(e) => update({
                                'email': e.target.value
                            })}
                            invalid={!!errors?.email}
                            errorMessage="Please provide an email."
                        />
                        <InputCustom
                            newLabel="Phone Number"
                            placeholder="Phone Number"
                            required
                            checkValid={false}
                            value={getValue('mobile_number')}
                            onChange={(e) => update({'mobile_number': e.target.value})}
                            invalid={!!errors?.mobile_number}
                            errorMessage="Please provide a phone number."
                        />
                        <InputCustom
                            newLabel="Bank account number"
                            placeholder="Bank account number"
                            value={getValue('bank_account_number')}
                            onChange={(e) => update({'bank_account_number': e.target.value})}
                        />
                    </Col>

                    <Col md={4}>
                        <InputCustom
                            newLabel="Full Name"
                            required
                            placeholder="Full Name"
                            value={getValue('full_name')}
                            onChange={(e) => update({'full_name': e.target.value})}
                            invalid={!!errors?.full_name}
                            errorMessage="The full name field is required."
                            checkValid={false}
                        />
                        <InputCustom
                            newLabel="N.I Number"
                            placeholder="N.I Number"
                            value={getValue('national_insurance_number')}
                            onChange={(e) => update({'national_insurance_number': e.target.value})}
                        />
                        <InputCustom
                            newLabel="D/L Number"
                            placeholder="D/L Number"
                            required
                            checkValid={false}
                            value={getValue('driver_license_number')}
                            onChange={(e) => update({'driver_license_number': e.target.value})}
                            invalid={!!errors?.driver_license_number}
                            errorMessage="Please provide a D/L Number."
                        />
                    </Col>

                    <Col sm={12} className="mt-3 mb-4">
                        <h2>Driving licence details</h2>
                    </Col>

                    <Col sm={12}>
                        <Row>
                            <Col md={3}>
                                <FormGroup className="d-flex align-items-center">
                                    <label className="form-control-label w-50">D/L Number</label>
                                    <Input
                                        type="text"
                                        placeholder="D/L Number"
                                        value={getValue('driver_license_number')}
                                        onChange={(e) => update({'driver_license_number': e.target.value})}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup className="d-flex align-items-center">
                                    <label className="form-control-label w-50">Country of issue</label>
                                    <Input
                                        type="text"
                                        placeholder="Country of issue"
                                        value={getValue('country_of_issue')}
                                        onChange={(e) => update({'country_of_issue': e.target.value})}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <InputCustom
                                    type="date"
                                    newLabel="Licence expiry date"
                                    value={getValue('licence_expiry_date')}
                                    onChange={(e) => update({'licence_expiry_date': e.target.value})}
                                    invalid={!!errors?.licence_expiry_date}
                                    errorMessage={errors?.licence_expiry_date}
                                />
                            </Col>
                            <Col md={3}>
                                <InputCustom
                                    type="date"
                                    newLabel="Licence issue date"
                                    value={getValue('licence_issue_date')}
                                    onChange={(e) => update({'licence_issue_date': e.target.value})}
                                    invalid={!!errors?.licence_issue_date}
                                    errorMessage={errors?.licence_issue_date}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>

                    <div className="d-flex flex-row justify-content-end">
                        <ButtonContainer onClick={() => goList(true)}>Cancel</ButtonContainer>
                        <ButtonContainer reverse onClick={() => handleSave()}>Save</ButtonContainer>
                    </div>
            </CardBody>
        </Card>
    );
}